import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

/**
 * Phase 2: SelectionChannel Store 구현
 *
 * 목록에서 선택된 항목 정보를 관리하고, 구독자들에게 변경사항을 전파합니다.
 * dedup 로직을 통해 동일한 선택이 재호출되는 것을 방지합니다.
 */

/**
 * Selection Mode: 선택 모드
 * - 'single': 단일 선택
 * - 'multi': 다중 선택
 *
 * @note Phase 2는 single만 지원, multi는 Phase 4에서 구현
 */
export type SelectionMode = 'single' | 'multi';

/**
 * 선택 데이터 구조
 *
 * SelectionChannel에 저장되는 선택 정보입니다.
 * viewMode는 저장하지 않으며, Provider가 관리합니다.
 */
export interface SelectionData<T = unknown> {
  /**
   * 선택된 항목 ID 배열
   * @example ['zone-123']
   * @example ['record-1', 'record-2', 'record-3']
   */
  ids: string[];

  /**
   * 선택 모드
   */
  mode: SelectionMode;

  /**
   * 선택된 항목 데이터 배열 (옵션)
   * - 다중 선택 시 비교 뷰 등에서 추가 API 호출 없이 데이터 사용 가능
   * - ids와 동일한 순서로 저장됨
   * @example [{ id: 'zone-123', name: 'my-zone' }]
   */
  items?: T[];
}

/**
 * 구독 콜백 타입
 */
type SelectionCallback = (selection: SelectionData | null) => void;

/**
 * SelectionChannel Store 상태
 */
interface SelectionChannelState {
  /**
   * Scope별 선택 상태 Map
   */
  selections: Map<string, SelectionData>;

  /**
   * Scope별 구독자 Map
   * @internal
   */
  subscribers: Map<string, Set<SelectionCallback>>;
}

/**
 * SelectionChannel Store 액션
 */
interface SelectionChannelActions {
  /**
   * 선택 초기화
   *
   * @param scopeKey - Scope 키
   */
  clearSelection(scopeKey: string): void;

  /**
   * 선택 조회 (동기)
   *
   * @param scopeKey - Scope 키
   * @returns 선택 데이터 또는 null
   */
  getSelection(scopeKey: string): SelectionData | null;

  /**
   * 선택 이벤트 발행
   *
   * 동일한 selection이 재호출되면 no-op 처리됩니다 (dedup).
   *
   * @param scopeKey - Scope 키
   * @param selection - 선택 데이터
   */
  publishSelection(scopeKey: string, selection: SelectionData): void;

  /**
   * 선택 변경 구독
   *
   * @param scopeKey - Scope 키
   * @param callback - 선택 변경 시 호출될 콜백
   * @returns unsubscribe 함수
   */
  subscribeSelection(scopeKey: string, callback: SelectionCallback): () => void;
}

export type SelectionChannelStore = SelectionChannelState & SelectionChannelActions;

const STORE_NAME = 'SELECTION_CHANNEL';

/**
 * Deep equality 비교 (배열 비교용)
 */
const areArraysEqual = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  return a.every((item, index) => item === b[index]);
};

/**
 * SelectionData deep equality 비교
 * items는 참조 비교만 수행 (동일 배열이면 같은 선택으로 간주)
 */
const areSelectionsEqual = (a: SelectionData, b: SelectionData): boolean => {
  return a.mode === b.mode && areArraysEqual(a.ids, b.ids) && a.items === b.items;
};

/**
 * Phase 2: SelectionChannel Store
 *
 * 선택 이벤트를 발행/구독하는 채널입니다.
 * Scope별로 선택 상태를 관리하며, dedup 로직을 통해 중복 발행을 방지합니다.
 */
export const useSelectionChannel = create<SelectionChannelStore>()(
  devtools(
    (set, get) => ({
      selections: new Map(),
      subscribers: new Map(),

      publishSelection: (scopeKey, selection) => {
        const currentSelection = get().selections.get(scopeKey);

        // Dedup: 동일한 선택이면 no-op
        if (currentSelection && areSelectionsEqual(currentSelection, selection)) {
          return;
        }

        // 선택 상태 업데이트
        set(
          state => {
            const nextSelections = new Map(state.selections);
            nextSelections.set(scopeKey, selection);
            return { selections: nextSelections };
          },
          undefined,
          `${STORE_NAME}/publishSelection`,
        );

        // 구독자들에게 변경사항 전파 (최신 상태에서 가져오기)
        const callbacks = get().subscribers.get(scopeKey);
        if (callbacks) {
          callbacks.forEach(callback => {
            try {
              callback(selection);
            } catch (error) {
              console.error(
                `[SelectionChannel] Error in subscription callback for ${scopeKey}:`,
                error,
              );
            }
          });
        }
      },

      clearSelection: scopeKey => {
        const currentSelection = get().selections.get(scopeKey);

        // 이미 선택이 없으면 no-op
        if (!currentSelection) {
          return;
        }

        // 선택 상태 제거
        set(
          state => {
            const nextSelections = new Map(state.selections);
            nextSelections.delete(scopeKey);
            return { selections: nextSelections };
          },
          undefined,
          `${STORE_NAME}/clearSelection`,
        );

        // 구독자들에게 null 전파 (최신 상태에서 가져오기)
        const callbacks = get().subscribers.get(scopeKey);
        if (callbacks) {
          callbacks.forEach(callback => {
            try {
              callback(null);
            } catch (error) {
              console.error(
                `[SelectionChannel] Error in subscription callback for ${scopeKey}:`,
                error,
              );
            }
          });
        }
      },

      getSelection: scopeKey => {
        return get().selections.get(scopeKey) ?? null;
      },

      subscribeSelection: (scopeKey, callback) => {
        // 구독자 추가
        set(
          state => {
            const nextSubscribers = new Map(state.subscribers);
            const callbacks = nextSubscribers.get(scopeKey) || new Set();
            callbacks.add(callback);
            nextSubscribers.set(scopeKey, callbacks);
            return { subscribers: nextSubscribers };
          },
          undefined,
          `${STORE_NAME}/subscribe`,
        );

        // 현재 선택 상태를 즉시 전달
        const currentSelection = get().selections.get(scopeKey) ?? null;
        try {
          callback(currentSelection);
        } catch (error) {
          console.error(
            `[SelectionChannel] Error in initial subscription callback for ${scopeKey}:`,
            error,
          );
        }

        // unsubscribe 함수 반환
        return () => {
          set(
            state => {
              const nextSubscribers = new Map(state.subscribers);
              const callbacks = nextSubscribers.get(scopeKey);
              if (callbacks) {
                callbacks.delete(callback);
                if (callbacks.size === 0) {
                  nextSubscribers.delete(scopeKey);
                }
              }
              return { subscribers: nextSubscribers };
            },
            undefined,
            `${STORE_NAME}/unsubscribe`,
          );
        };
      },
    }),
    { name: STORE_NAME },
  ),
);
